import { readStamped, writeStamped, safeRemoveItem, clearStampedForOwner } from './activeRoleStorage';

/**
 * Time's calendar (issue 682): project time dropped where no working time covers
 * it asks "add working time here as well?" — and "don't ask again" remembers the
 * YES, so later drops add the covering working time silently.
 *
 * Lives here rather than in time-ui because it has a SECOND reader —
 * `forgetWorkspaceLocalState`, which drops it when the workspace it was stamped
 * against has been erased (the same reason as `demoStageIntro.ts`). Stamped per
 * user and keyed per workspace, so the next account in this browser is asked.
 */
const VALUE_PREFIX = 'timeAddWorkTimeOnDrop:';
const OWNER_PREFIX = 'timeAddWorkTimeOnDropOwner:';

function keysFor(tenantSlug: string | null | undefined): { valueKey: string; ownerKey: string } | null {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return null;
  return { valueKey: `${VALUE_PREFIX}${slug}`, ownerKey: `${OWNER_PREFIX}${slug}` };
}

/** Whether this user said "always add the working time" for this workspace. */
export function addWorkTimeOnDropRemembered(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  const keys = keysFor(tenantSlug);
  if (!userId || !keys) return false;
  return readStamped(keys.valueKey, keys.ownerKey, userId) === '1';
}

/** Remember the YES. Without a workspace or an owner there is nothing to stamp, and she is simply asked again. */
export function rememberAddWorkTimeOnDrop(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = keysFor(tenantSlug);
  if (!userId || !keys) return;
  writeStamped(keys.valueKey, keys.ownerKey, userId, '1');
}

/** Forget it, for every account in this browser — the workspace itself is gone. */
export function clearAddWorkTimeOnDrop(tenantSlug: string | null | undefined): void {
  const keys = keysFor(tenantSlug);
  if (!keys) return;
  safeRemoveItem(keys.valueKey);
  safeRemoveItem(keys.ownerKey);
}

/** Forget one account's YES only — a colleague's stays. */
export function clearAddWorkTimeOnDropForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = keysFor(tenantSlug);
  if (!keys) return;
  clearStampedForOwner(keys.valueKey, keys.ownerKey, userId);
}
